import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
  limit,
} from 'firebase/firestore';
import { db } from '../firebase';

const SEQ_DOC = doc(db, 'siteSettings', 'registrationSeq');

function dateKey(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}${m}${d}`;
}

function formatRegNumber(dateStr, seq) {
  const digits = String(seq).padStart(seq < 1000 ? 3 : seq < 10000 ? 4 : 5, '0');
  return `SBA-${dateStr}-${digits}`;
}

async function registrationNumberTaken(regNumber) {
  const [usersSnap, subsSnap] = await Promise.all([
    getDocs(
      query(collection(db, 'users'), where('registrationNumber', '==', regNumber), limit(1))
    ),
    getDocs(
      query(collection(db, 'subscriptions'), where('registrationNumber', '==', regNumber), limit(1))
    ),
  ]);
  return !usersSnap.empty || !subsSnap.empty;
}

/**
 * Allocate a unique registration number: SBA-YYYYMMDD-### (3–5 digits).
 * Uses siteSettings/registrationSeq; falls back to random digits if the counter is blocked.
 */
export async function allocateRegistrationNumber() {
  const today = dateKey();

  try {
    const allocated = await runTransaction(db, async (tx) => {
      const snap = await tx.get(SEQ_DOC);
      let seq = 1;
      if (snap.exists()) {
        const data = snap.data() || {};
        if (data.lastDate === today) {
          seq = (Number(data.lastSeq) || 0) + 1;
        }
      }
      if (seq > 99999) throw new Error('Daily registration sequence exhausted.');
      tx.set(
        SEQ_DOC,
        {
          lastDate: today,
          lastSeq: seq,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      return formatRegNumber(today, seq);
    });

    if (!(await registrationNumberTaken(allocated))) return allocated;
  } catch (err) {
    console.warn('Registration sequence transaction failed, using fallback:', err?.message || err);
  }

  // Fallback: random 3–5 digit suffix with uniqueness check
  for (let attempt = 0; attempt < 12; attempt += 1) {
    const len = attempt < 4 ? 3 : attempt < 8 ? 4 : 5;
    const max = 10 ** len;
    const seq = Math.floor(Math.random() * (max - 1)) + 1;
    const candidate = formatRegNumber(today, seq);
    if (!(await registrationNumberTaken(candidate))) return candidate;
  }

  // Last resort — include time fragment for uniqueness
  const stamp = String(Date.now()).slice(-5);
  return `SBA-${today}-${stamp}`;
}

/** Return existing reg # for a user, or allocate and persist one. */
export async function ensureUserRegistrationNumber(userId, existingNumber = '') {
  if (existingNumber) return existingNumber;
  if (!userId) return allocateRegistrationNumber();

  const userRef = doc(db, 'users', userId);
  const snap = await getDoc(userRef);
  if (snap.exists()) {
    const current = snap.data()?.registrationNumber || '';
    if (current) return current;
  }

  const regNumber = await allocateRegistrationNumber();
  try {
    await updateDoc(userRef, {
      registrationNumber: regNumber,
      registrationAssignedAt: serverTimestamp(),
    });
  } catch (err) {
    console.warn('Could not persist registration number on user:', err?.message || err);
  }
  return regNumber;
}

export async function findUserByRegistrationNumber(regNumber) {
  const qText = String(regNumber || '').trim().toUpperCase();
  if (!qText) return null;
  const snap = await getDocs(
    query(collection(db, 'users'), where('registrationNumber', '==', qText), limit(1))
  );
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() };
}
